// Updated File: src/pages/SearchResult.jsx
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { productsAPI } from '../services/api';
import { SlidersHorizontal, X, ChevronDown, Grid, List } from 'lucide-react';
import './css/SearchResult.css';

const SearchResult = ({ openCart }) => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroImage, setHeroImage] = useState(null);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [inStock, setInStock] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchData = async () => {
      if (!query) {
        setProducts([]);
        setFilteredProducts([]);
        setLoading(false);
        setHeroImage(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const productsResponse = await productsAPI.search(query);
        const fetchedProducts = productsResponse.data || [];
        setProducts(fetchedProducts);
        setFilteredProducts(fetchedProducts);
        // Set hero background to first product's image if available
        if (fetchedProducts.length > 0) {
          setHeroImage(fetchedProducts[0].imageUrl);
        } else {
          setHeroImage(null);
        }

      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.response?.data?.message || err.message || 'Impossible de charger les resultats de recherche');
        setProducts([]);
        setFilteredProducts([]);
        setHeroImage(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query]);

  useEffect(() => {
    applyFilters();
  }, [products, sortBy, priceRange, inStock]);

  const applyFilters = () => {
    let filtered = [...products];

    // Stock filter
    if (inStock) {
      filtered = filtered.filter(product => product.stock > 0);
    }

    // Price range filter
    if (priceRange.min !== '') {
      filtered = filtered.filter(product => {
        const price = product.salePrice !== null ? product.salePrice : product.regularPrice;
        return price >= parseFloat(priceRange.min);
      });
    }
    if (priceRange.max !== '') {
      filtered = filtered.filter(product => {
        const price = product.salePrice !== null ? product.salePrice : product.regularPrice;
        return price <= parseFloat(priceRange.max);
      });
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.salePrice !== null ? a.salePrice : a.regularPrice;
          const priceB = b.salePrice !== null ? b.salePrice : b.regularPrice;
          return priceA - priceB;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.salePrice !== null ? a.salePrice : a.regularPrice;
          const priceB = b.salePrice !== null ? b.salePrice : b.regularPrice;
          return priceB - priceA;
        });
        break;
      case 'name-az':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-za':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        filtered.sort((a, b) => b.id - a.id);
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setSortBy('default');
    setPriceRange({ min: '', max: '' });
    setInStock(false);
  };

  const hasActiveFilters = () => {
    return sortBy !== 'default' || priceRange.min !== '' || priceRange.max !== '' || inStock;
  };

  if (loading) {
    return (
      <div className="sr-loading">
        <div className="sr-spinner"></div>
        <p>Recherche de "{query}"...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sr-error">
        <h2>Erreur lors du chargement des resultats</h2>
        <p>{error}</p>
        <p>Veuillez verifier que le serveur backend est en cours d'execution et accessible.</p>
      </div>
    );
  }

  return (
    <div className="sr-container">
      {/* Search Hero Section */}
      <div className="sr-hero" style={{
        backgroundImage: heroImage 
          ? `linear-gradient(135deg, rgba(38, 38, 37, 0.92) 0%, rgba(0, 0, 0, 0.88) 100%), url(${heroImage})`
          : 'linear-gradient(135deg, #262625 0%, #000000 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div className="sr-hero-content">
          <div className="sr-hero-text">
            <h1 className="sr-hero-title">Resultats de recherche pour "{query}"</h1>
            <div className="sr-hero-stats">
              <span className="sr-hero-stat">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'Produit' : 'Produits'}
              </span>
              {filteredProducts.length !== products.length && (
                <span className="sr-hero-stat-filtered">
                  (Filtre sur {products.length})
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sr-toolbar">
        <button 
          className={`sr-filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <SlidersHorizontal size={18} />
          <span>Filtres</span>
          {hasActiveFilters() && <span className="sr-filter-badge"></span>}
        </button>

        <div className="sr-toolbar-right">
          <div className="sr-view-toggle">
            <button
              className={`sr-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vue grille"
            >
              <Grid size={18} />
            </button>
            <button
              className={`sr-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vue liste"
            >
              <List size={18} />
            </button>
          </div>

          <div className="sr-sort-wrapper">
            <label htmlFor="sort-select" className="sr-sort-label">Trier par :</label>
            <div className="sr-select-wrapper">
              <select
                id="sort-select"
                className="sr-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Par defaut</option>
                <option value="price-low">Prix : du plus bas au plus haut</option>
                <option value="price-high">Prix : du plus haut au plus bas</option>
                <option value="name-az">Nom : A a Z</option>
                <option value="name-za">Nom : Z a A</option>
                <option value="newest">Plus recents</option>
              </select>
              <ChevronDown size={16} className="sr-select-icon" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="sr-main">
        {/* Filters Sidebar */}
        <aside className={`sr-filters ${showFilters ? 'show' : ''}`}>
          <div className="sr-filters-header">
            <h3 className="sr-filters-title">Filtres</h3>
            <button 
              className="sr-filters-close"
              onClick={() => setShowFilters(false)}
            >
              <X size={20} />
            </button>
          </div>

          <div className="sr-filters-content">
            {/* Price Range */}
            <div className="sr-filter-group">
              <h4 className="sr-filter-title">Plage de prix</h4>
              <div className="sr-price-inputs">
                <div className="sr-price-input-wrapper">
                  <label className="sr-price-label">Min</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="sr-price-input"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                    min="0"
                  />
                </div>
                <div className="sr-price-input-wrapper">
                  <label className="sr-price-label">Max</label>
                  <input
                    type="number"
                    placeholder="Sans limite"
                    className="sr-price-input"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Availability */}
            <div className="sr-filter-group">
              <h4 className="sr-filter-title">Disponibilite</h4>
              <label className="sr-checkbox-label">
                <input
                  type="checkbox"
                  className="sr-checkbox"
                  checked={inStock}
                  onChange={(e) => setInStock(e.target.checked)}
                />
                <span className="sr-checkbox-text">En stock uniquement</span>
              </label>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters() && (
              <button 
                className="sr-clear-filters"
                onClick={clearFilters}
              >
                Effacer tous les filtres
              </button>
            )}
          </div>
        </aside>

        {/* Products Grid */}
        <div className="sr-products-section">
          {filteredProducts.length === 0 ? (
            <div className="sr-no-products">
              <p className="sr-no-products-title">Aucun produit trouve</p>
              <p className="sr-no-products-text">
                {hasActiveFilters() 
                  ? 'Essayez d\'ajuster vos filtres pour voir plus de resultats.'
                  : (
                    <>
                      Aucun produit trouve pour "{query}". <Link to="/">Continuer les achats</Link>
                    </>
                  )}
              </p>
              {hasActiveFilters() && (
                <button 
                  className="sr-clear-filters-btn"
                  onClick={clearFilters}
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          ) : (
            <div className={`sr-product-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  openCart={openCart}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      {showFilters && (
        <div 
          className="sr-filters-overlay"
          onClick={() => setShowFilters(false)}
        ></div>
      )}
    </div>
  );
};

export default SearchResult;
